// UI state for the Questionnaire Design page (PRD 05 §5).
// Server data lives in React Query — this store only tracks selection,
// tree expansion, and the disease filter.
//
// Modal/slide-panel state is intentionally NOT here yet — it lands with the
// matching slice (mobile preview = S6, copy modal = S5, edit panel = S3).
// Keeping the store narrow now avoids dead fields piling up before their
// owners ship.

import { create } from 'zustand';

import type { Target } from '../types/questionnaire-design.types';

// Disease filter values:
//   - ''          → all
//   - 'COMMON'    → applicability=COMMON questions only
//   - 'GRP:<name>'→ scoped to a disease group
//   - '<icd>'     → scoped to an ICD-10 code (e.g. 'F32')
export type DiseaseFilterValue = '' | 'COMMON' | `GRP:${string}` | string;

// Edit-panel modes: create (no qid), edit (existing qid), add-sub (no qid + a
// parent qid). The store only tracks intent; the panel component pulls the
// existing question via React Query at render time.
export type EditPanelMode = 'create' | 'edit' | 'add-sub';

export interface EditPanelState {
  open: boolean;
  mode: EditPanelMode;
  // For 'edit', the qid being edited.
  qid: string | null;
  // For 'add-sub', the parent qid. Also used by 'edit' to remember the
  // parent when the user is editing a sub-question.
  parentQid: string | null;
  // Default target for 'create' (driven by which column's `+ 추가` opened the
  // panel). 'edit' ignores this — the existing question's target wins.
  defaultTarget: Target | null;
}

// PRD 05 §3.8 — copy modal state. `targetColumn` records which column's `+
// 복사하여 추가` button opened it, so the server-side copy can stamp the new
// question's `target` field.
// SHCT-862 — PRD 05 v1.34 §3.8.1 gives 복사 two entry points, and the modal has to know which
// one opened it: the candidate pool and where the copy lands both differ.
//   COLUMN (`+ 복사하여 추가`, 컬럼 헤더) → top-level questions only, appended to that column.
//   SUB    (`복사하여 하위문항추가`, 메인 문항 카드) → sub-questions included, attached under
//           `parentQid`.
// §3.8.1 is explicit about why they cannot share one pool: offering sub-questions from the column
// header would land one at top level and turn it into a main question, breaking the
// 「하위문항 자동 변환 차단」 rule §3.8.3 sets.
export type CopyEntryPoint = 'COLUMN' | 'SUB';

export interface CopyModalState {
  open: boolean;
  targetColumn: Target | null;
  entryPoint: CopyEntryPoint;
  // Set only for the SUB entry point — the main question the copy attaches to.
  parentQid: string | null;
}

interface QuestionnaireDesignPageState {
  selectedItemId: string | null;
  // Includes areas/subs so the highlight follows the user's last click, not
  // just the loaded item.
  selectedTreeNodeId: string | null;
  expandedNodes: Record<string, boolean>;
  diseaseFilter: DiseaseFilterValue;
  editPanel: EditPanelState;
  copyModal: CopyModalState;

  selectItem: (id: string) => void;
  toggleNode: (id: string) => void;
  expandNode: (id: string) => void;
  setDiseaseFilter: (value: DiseaseFilterValue) => void;
  openCreatePanel: (target: Target) => void;
  openEditPanel: (qid: string) => void;
  openAddSubPanel: (parentQid: string) => void;
  closeEditPanel: () => void;
  openCopyModal: (target: Target) => void;
  // SHCT-862 — the 메인 문항 카드 entry point. `target` comes from the parent's own column, so
  // the copy cannot cross columns away from the question it attaches to.
  openCopySubModal: (parentQid: string, target: Target) => void;
  closeCopyModal: () => void;
  reset: () => void;
}

// No preselection against the real backend: item ids are server-issued PID
// strings (P-XXXX), so any hard-coded id would 404. The user picks an item
// from the tree; until then the editor shows its "select an item" empty state.
const INITIAL_SELECTION: string | null = null;

const CLOSED_EDIT_PANEL: EditPanelState = {
  open: false,
  mode: 'create',
  qid: null,
  parentQid: null,
  defaultTarget: null,
};

const CLOSED_COPY_MODAL: CopyModalState = {
  open: false,
  targetColumn: null,
  entryPoint: 'COLUMN',
  parentQid: null,
};

const INITIAL: Pick<
  QuestionnaireDesignPageState,
  | 'selectedItemId'
  | 'selectedTreeNodeId'
  | 'expandedNodes'
  | 'diseaseFilter'
  | 'editPanel'
  | 'copyModal'
> = {
  selectedItemId: INITIAL_SELECTION,
  selectedTreeNodeId: INITIAL_SELECTION,
  // Start fully collapsed; the user expands branches as they navigate the
  // server-issued tree.
  expandedNodes: {},
  diseaseFilter: '',
  editPanel: CLOSED_EDIT_PANEL,
  copyModal: CLOSED_COPY_MODAL,
};

export const useQuestionnaireDesignPageStore = create<QuestionnaireDesignPageState>(
  (set) => ({
    ...INITIAL,

    selectItem: (id) =>
      set({ selectedItemId: id, selectedTreeNodeId: id }),

    toggleNode: (id) =>
      set((s) => {
        const next = { ...s.expandedNodes };
        if (next[id]) delete next[id];
        else next[id] = true;
        return { expandedNodes: next, selectedTreeNodeId: id };
      }),

    expandNode: (id) =>
      set((s) =>
        s.expandedNodes[id]
          ? s
          : { expandedNodes: { ...s.expandedNodes, [id]: true } },
      ),

    setDiseaseFilter: (value) => set({ diseaseFilter: value }),

    openCreatePanel: (target) =>
      set({
        editPanel: {
          open: true,
          mode: 'create',
          qid: null,
          parentQid: null,
          defaultTarget: target,
        },
      }),

    openEditPanel: (qid) =>
      set({
        editPanel: {
          open: true,
          mode: 'edit',
          qid,
          parentQid: null,
          defaultTarget: null,
        },
      }),

    openAddSubPanel: (parentQid) =>
      set({
        editPanel: {
          open: true,
          mode: 'add-sub',
          qid: null,
          parentQid,
          defaultTarget: null,
        },
      }),

    closeEditPanel: () => set({ editPanel: CLOSED_EDIT_PANEL }),

    openCopyModal: (target) =>
      set({
        copyModal: { open: true, targetColumn: target, entryPoint: 'COLUMN', parentQid: null },
      }),
    openCopySubModal: (parentQid, target) =>
      set({
        copyModal: { open: true, targetColumn: target, entryPoint: 'SUB', parentQid },
      }),
    closeCopyModal: () => set({ copyModal: CLOSED_COPY_MODAL }),

    reset: () => set(INITIAL),
  }),
);
